import React, { useState, useEffect, useRef } from 'react';
import { ChevronDownIcon } from '../icons';
import { fetchLocationByQuery } from '../../features/location/locationSlice';
import { useAppDispatch } from '../../app/hooks';

interface SearchDropdownProps {
    items: Array<{
        id: number;
        name: string;
        [key: string]: any;
    }>;
    buttonText: string;
    selectedItem: any;
    onChange: (item: any) => void;
    disabled?: boolean;
    isLoading?: boolean;
}

const JobEduLocationModal: React.FC<SearchDropdownProps> = ({ 
    items = [], 
    buttonText, 
    selectedItem, 
    onChange,
    disabled = false,
    isLoading = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null); // Ref for the dropdown container

    const dispatch = useAppDispatch();
    // Ensure items is always an array
    // const safeItems = Array.isArray(items) ? items : [];

    const safeItems = Array.isArray(items) ? items.filter(item => 
        item && typeof item === 'object' && 'locationName' in item && typeof item.locationName === 'string'
    ) : [];
    
    const toggleDropdown = () => {
        if (!disabled && !isLoading) {
            setIsOpen(!isOpen);
        }
    };


    // const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     setSearchTerm(event.target.value.toLowerCase());
    //     setSelectedIndex(-1);
    // };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const searchTerm = event.target.value.toLowerCase();
        setSearchTerm(searchTerm);
        setSelectedIndex(-1);
    
        // Dispatch the API call
        if (searchTerm.trim()) {
            dispatch(fetchLocationByQuery(searchTerm));
        }
    };
    
    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (!isOpen) return;
    
        const filteredItems = safeItems.filter(item => 
            item.locationName.toLowerCase().includes(searchTerm)
        );
    
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setSelectedIndex((prevIndex) => {
                const newIndex = Math.min(prevIndex + 1, filteredItems.length - 1);
                scrollIntoView(newIndex);
                return newIndex;
            });
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setSelectedIndex((prevIndex) => {
                const newIndex = Math.max(prevIndex - 1, 0);
                scrollIntoView(newIndex);
                return newIndex;
            });
        } else if (event.key === 'Enter' && selectedIndex >= 0) {
            event.preventDefault();
            handleItemClick(filteredItems[selectedIndex]);
        } else if (event.key === 'Escape') {
            setIsOpen(false);
        }
    };

    const scrollIntoView = (index: number) => {
        if (listRef.current) {
            const selectedElement = listRef.current.children[index];
            if (selectedElement) {
                selectedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    };

    const handleItemClick = (item: any) => {
        setSearchTerm('');
        setIsOpen(false);
        onChange(item);
    };

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const filteredItems = safeItems.filter(item =>
        item.locationName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    // const filteredItems = safeItems.filter(item => {
    //     return item.locationName.toLowerCase().includes(searchTerm.toLowerCase()); // Filter condition
    // });
    

    return (
        <div className="flex items-center justify-center w-full">
            <div className="relative group w-full" ref={dropdownRef}>
                {/* {!isOpen && (
                    <button
                        onClick={toggleDropdown}
                        disabled={disabled}
                        className={`inline-flex justify-between items-center w-full pl-3 pr-2 py-2 h-[42px] text-sm font-medium text-gray-700 bg-white border
                            border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500
                            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <span className="mr-2">{selectedItem ? selectedItem.name : buttonText}</span>
                        <ChevronDownIcon aria-hidden="true" className="h-4 w-4 text-gray-400" />
                    </button>
                )} */}
                  {!isOpen && (
                    <button
                        onClick={toggleDropdown}
                        disabled={disabled || isLoading}
                        className={`inline-flex justify-between items-center w-full pl-3 pr-2 py-[7px] font-normal leading-5  bg-white hover:border-[#e5e7eb] 
                            border-solid border-[#e5e7eb]  rounded-[0.375rem] border
                            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500
                            ${(disabled || isLoading) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <span className="mr-2 text-sm text-[#374151] font-normal leading-5 line-clamp-1 text-left">{selectedItem ? selectedItem.locationName || selectedItem : buttonText}</span>
                        {isLoading ? (
                             <div className="flex items-center justify-center relative z-50" style={{ height: 'calc(100vh - 191px)' }}>
                            <div className="simple-spinner-loader">
                              <span className='!w-4 !h-4'></span>
                            </div>
                          </div>
                        ) : (
                            <ChevronDownIcon />
                        )}
                    </button>
                )}
                {isOpen && (
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Search country"
                        autoComplete="off"
                        value={searchTerm}
                        onChange={handleSearch}
                        onKeyDown={handleKeyDown}
                        className="block w-full px-4 py-[7px] text-sm text-[#374151] placeholder:text-[13px] font-normal !outline-none 
                        focus:outline-none focus:outline-[-2px] bg-white hover:border-[#e5e7eb] 
                            border-solid border-[#e5e7eb]  rounded-[0.375rem] border "
                    />
                )}
                 {isOpen && !isLoading && filteredItems.length > 0 && (

                    <div className="w-full z-10 absolute right-0 mt-2 rounded-md  bg-white ring-1 ring-black ring-opacity-5 p-1 space-y-1">
                     
                        <div ref={listRef} className="overflow-y-auto max-h-32">
                            {filteredItems.map((item, index) => (                                
                                <a
                                    key={item.id}
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleItemClick(item);
                                    }}
                                    className={`block px-4 py-2  hover:bg-gray-100 active:bg-indigo-100 cursor-pointer text-[13px] text-[#374151] placeholder:text-[13px] font-normal line-clamp-1 rounded-[0.375rem] ${
                                        selectedIndex === index ? 'bg-indigo-100' : ''
                                    }`}
                                >
                                    {item.locationName}
                                </a>
                            ))}
                        </div>
                    </div>
                )}
                  {/* {!isOpen && !isLoading && (
                     <div className="flex items-center justify-center relative z-50" style={{ height: 'calc(100vh - 191px)' }}>
                     <div className="simple-spinner-loader">
                       <span></span>
                     </div>
                   </div>
                )} */}
            </div>
        </div>
    );
};

export default JobEduLocationModal;